export default function TermsPage() {
  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-300">
      <div className="max-w-3xl mx-auto px-6 py-16">
        <div className="mb-10">
          <a href="/" className="text-cyan-400 text-sm hover:underline font-body">← Back to AceIt AI</a>
        </div>
        <h1 className="text-4xl font-heading font-black text-white mb-2">Terms &amp; Conditions</h1>
        <p className="text-zinc-500 text-sm font-body mb-10">Last updated: July 2026 &nbsp;|&nbsp; Effective immediately upon account creation</p>

        <div className="space-y-8 font-body text-sm leading-relaxed">

          <section>
            <h2 className="text-white text-lg font-heading font-bold mb-3">1. Acceptance of Terms</h2>
            <p>By creating an account on AceIt AI ("<strong>Platform</strong>", "<strong>we</strong>", "<strong>us</strong>"), you ("<strong>User</strong>") agree to be bound by these Terms &amp; Conditions. If you do not agree, you must not use the Platform. These Terms apply to all users including students, parents, and school administrators.</p>
          </section>

          <section>
            <h2 className="text-white text-lg font-heading font-bold mb-3">2. Description of Service</h2>
            <p>AceIt AI is an AI-powered educational platform providing:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1 text-zinc-400">
              <li>AI-based tutoring and explanations (powered by third-party AI models)</li>
              <li>Practice quizzes and mock examinations</li>
              <li>Curriculum-aligned study materials</li>
              <li>Progress tracking and leaderboards</li>
              <li>Subscription-based premium features</li>
            </ul>
          </section>

          <section>
            <h2 className="text-white text-lg font-heading font-bold mb-3">3. Eligibility</h2>
            <p>The Platform is intended for students enrolled in recognized schools. Users under 13 years of age must have parental or guardian consent. By registering, you confirm that either (a) you are 13 or older, or (b) you have obtained verifiable parental consent.</p>
          </section>

          <section>
            <h2 className="text-white text-lg font-heading font-bold mb-3">4. Artificial Intelligence (AI) Disclaimer</h2>
            <p className="mb-2">AceIt AI uses large language models (LLMs) provided by third-party services including but not limited to OpenRouter and DeepSeek. By using the AI Tutor, you acknowledge and agree that:</p>
            <ul className="list-disc pl-6 space-y-1 text-zinc-400">
              <li><strong className="text-zinc-200">AI responses may contain inaccuracies.</strong> Always verify important information with your teacher or textbook.</li>
              <li>Your questions and chat messages are processed by our AI provider's servers and are subject to their data policies.</li>
              <li>We do not guarantee the accuracy, completeness, or timeliness of AI-generated content.</li>
              <li>AI responses are for educational assistance only and do not constitute professional advice.</li>
              <li>We implement content filters to block inappropriate content, but cannot guarantee perfect filtering.</li>
              <li>We are not liable for any incorrect advice, errors, or omissions in AI-generated responses.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-white text-lg font-heading font-bold mb-3">5. Credits System</h2>
            <p>The Platform uses a credits-based system to manage usage of AI features:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1 text-zinc-400">
              <li>New accounts receive 100 free credits upon registration.</li>
              <li>Credits are consumed when using AI Tutor, Quiz Arena, and other AI-powered features.</li>
              <li>Credits are non-refundable once consumed.</li>
              <li>We reserve the right to modify credit costs at any time with reasonable notice.</li>
              <li>Unused credits in free accounts do not expire but may be subject to fair-use limits.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-white text-lg font-heading font-bold mb-3">6. Payments and Subscriptions</h2>
            <p className="mb-2">Premium features are available through paid subscriptions processed by a third-party payment gateway. By making a purchase, you agree that:</p>
            <ul className="list-disc pl-6 space-y-1 text-zinc-400">
              <li>All payments are final. Refunds are issued at our sole discretion and only in cases of technical errors on our end.</li>
              <li>Subscription charges are recurring unless cancelled before the renewal date.</li>
              <li>You are responsible for all applicable taxes in your jurisdiction.</li>
              <li>We are not liable for payment failures caused by your bank, card issuer, or payment gateway.</li>
              <li>Free trial periods (if offered) automatically convert to paid subscriptions unless cancelled.</li>
              <li>Payment data is securely handled by our payment processor and is never stored on our servers.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-white text-lg font-heading font-bold mb-3">7. User Conduct</h2>
            <p>You agree not to:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1 text-zinc-400">
              <li>Use the Platform for any unlawful purpose</li>
              <li>Attempt to circumvent credit limits or access controls</li>
              <li>Input explicit, offensive, or harmful content into the AI Tutor</li>
              <li>Share your account credentials with others</li>
              <li>Reverse-engineer, scrape, or copy Platform content at scale</li>
              <li>Impersonate other users, teachers, or administrators</li>
            </ul>
            <p className="mt-2">Violation of these rules may result in immediate account suspension without refund.</p>
          </section>

          <section>
            <h2 className="text-white text-lg font-heading font-bold mb-3">8. Intellectual Property</h2>
            <p>All Platform content including UI design, branding, AI-generated responses, and curriculum materials are the property of AceIt AI or its licensors. You are granted a limited, non-exclusive, non-transferable license to use the Platform for personal educational purposes only.</p>
          </section>

          <section>
            <h2 className="text-white text-lg font-heading font-bold mb-3">9. Limitation of Liability</h2>
            <p>To the maximum extent permitted by law, AceIt AI shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of the Platform, including but not limited to loss of data, examination failure, or reliance on AI-generated content. Our total liability shall not exceed the amount paid by you in the 30 days preceding the claim.</p>
          </section>

          <section>
            <h2 className="text-white text-lg font-heading font-bold mb-3">10. Account Termination</h2>
            <p>We reserve the right to suspend or terminate accounts that violate these Terms. You may delete your account at any time from your profile settings. Upon termination, your data is retained for 30 days before permanent deletion, except where required by law.</p>
          </section>

          <section>
            <h2 className="text-white text-lg font-heading font-bold mb-3">11. Changes to Terms</h2>
            <p>We may update these Terms periodically. Continued use of the Platform after changes constitutes acceptance of the new Terms. We will notify users of material changes via email or in-app notification.</p>
          </section>

          <section>
            <h2 className="text-white text-lg font-heading font-bold mb-3">12. Governing Law</h2>
            <p>These Terms are governed by the laws of India. Any disputes shall be subject to the exclusive jurisdiction of courts in India. If any provision is found unenforceable, the remaining provisions continue in full effect.</p>
          </section>

          <section>
            <h2 className="text-white text-lg font-heading font-bold mb-3">13. Contact</h2>
            <p>For questions about these Terms, contact us via the feedback button in the app or email us through your school's registered contact.</p>
          </section>

        </div>

        <div className="mt-12 pt-8 border-t border-white/10 flex gap-4 text-xs text-zinc-600 font-body">
          <a href="/privacy" className="hover:text-zinc-400">Privacy Policy</a>
          <span>·</span>
          <a href="/" className="hover:text-zinc-400">Home</a>
        </div>
      </div>
    </div>
  );
}
